import { inject, Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { map, Observable, of } from 'rxjs';
import { RadiusOfOperation } from '../../../../../../core/constants/radius-of-operation';
import {
    nameOf,
    tryParseToNumber,
} from '../../../../../../core/functions/string-helpers';
import { FormGroupType } from '../../../../../../core/models/form-group-type';
import { ActionCode } from '../../../../../../core/models/resources/action-code';
import { CoordinateIsInUSOrTerritoriesRequest } from '../../../../../../core/services/validation/models/coordinate-is-in-us-or-territories-request';
import { ValidationService } from '../../../../../../core/services/validation/validation.service';
import {
    LatitudeControlValidators,
    LongitudeControlValidators,
} from '../../../../shared/components/latitude-longitude-form/constants/latitude-longitude-control-validators';
import { AreaOfOperationCodes } from '../../../../shared/constants/area-of-operation-codes';
import { LocationTypeCodes } from '../../../../shared/constants/location-type-codes';
import { SchedulesDAndHSharedFormService } from '../../../shared/services/schedules-d-and-h-shared-form/schedules-d-and-h-shared-form.service';
import { ScheduleDStepOneRegExPattern } from '../../components/schedule-d-step-one/models/schedule-d-step-one-regex-pattern';
import { ScheduleD } from '../../models/schedule-d';
import { ScheduleDFormModel } from '../../models/schedule-d-form-model';
import { AreaOfOperationCode } from '../../types/area-of-operation-code';
import { ScheduleDBinaryLocationType } from '../../types/schedule-d-binary-location-type';
import { ScheduleDControlValidatorsMap } from '../../types/schedule-d-control-validators-map';
import { ScheduleDDefaultControlValuesMap } from '../../types/schedule-d-default-control-values-map';
import { ScheduleDLocationType } from '../../types/schedule-d-location-type';
import { ScheduleDStepControlsMap } from '../../types/schedule-d-step-controls-map';

const towerRegistrationNumberControl = nameOf<ScheduleDFormModel>(
    'towerRegistrationNumber',
);

@Injectable({
    providedIn: 'root',
})
export class ScheduleDFormService {
    private readonly validationService = inject(ValidationService);
    private readonly schedulesDAndHSharedFormService = inject(
        SchedulesDAndHSharedFormService,
    );

    readonly requiredControlsForAllTypes: (keyof ScheduleDFormModel)[] = [
        'locationTypeCode',
    ] as const;

    readonly commonNonFixedControls: (keyof ScheduleDFormModel)[] = [
        'areaOfOperationCode',
        'siteStatus',
        'clearanceIndicator',
        'nepa',
        'mexicoClearanceIndicator',
    ] as const;

    readonly locationTypeToControlsWithValidatorsMapping: ScheduleDStepControlsMap<ScheduleDLocationType> =
        {
            fixed: [
                ...this.requiredControlsForAllTypes,
                'locationName',
                'towerRegistrationNumber',
                'requireEWAToRegisterTower',
                'latitude',
                'longitude',
                'streetAddress',
                'city',
                'state',
                'county',
                'groundElevation',
                'heightOfSupportStructure',
                'overallHeightOfStructure',
                'structureTypeCode',
                'siteStatus',
                'quietZoneNotificationDate',
                'quietZoneConsent',
                'nepa',
                'mexicoClearanceIndicator',
            ],
            A: [
                ...this.requiredControlsForAllTypes,
                ...this.commonNonFixedControls,
                'correspondingFixedLocation',
                'radius',
            ],
            C: [
                ...this.requiredControlsForAllTypes,
                ...this.commonNonFixedControls,
                'state',
                'county',
            ],
            N: [
                ...this.requiredControlsForAllTypes,
                ...this.commonNonFixedControls,
            ],
            O: [
                ...this.requiredControlsForAllTypes,
                ...this.commonNonFixedControls,
                'areaOfOperationText',
            ],
            P: [
                ...this.requiredControlsForAllTypes,
                ...this.commonNonFixedControls,
                'latitude',
                'longitude',
                'radius',
                'city',
                'state',
                'county',
            ],
            S: [
                ...this.requiredControlsForAllTypes,
                ...this.commonNonFixedControls,
                'state',
            ],
            T: [
                ...this.requiredControlsForAllTypes,
                ...this.commonNonFixedControls,
                'state',
                'city',
            ],
            U: [
                ...this.requiredControlsForAllTypes,
                ...this.commonNonFixedControls,
            ],
            X: [
                ...this.requiredControlsForAllTypes,
                ...this.commonNonFixedControls,
                'state',
                'county',
            ],
        } as const;

    readonly defaultControlValuesMap: ScheduleDDefaultControlValuesMap = {
        quietZoneConsent: false,
    } as const;

    readonly controlValidators: ScheduleDControlValidatorsMap = {
        id: [],
        actionCode: [],
        isDeleted: [],
        tempId: [],
        isBeingEdited: [],
        number: [],
        locationTypeCode: [Validators.required],
        areaOfOperationCode: [Validators.required],
        locationName: [],
        towerRegistrationNumber: [
            Validators.pattern(
                ScheduleDStepOneRegExPattern.TowerRegistrationNumber,
            ),
        ],
        requireEWAToRegisterTower: [],
        latitude: LatitudeControlValidators,
        longitude: LongitudeControlValidators,
        radius: [
            Validators.required,
            Validators.min(RadiusOfOperation.minimum),
            Validators.max(RadiusOfOperation.maximum),
        ],
        streetAddress: [Validators.required],
        city: [Validators.required],
        state: [Validators.required],
        county: [],
        groundElevation: [Validators.required],
        heightOfSupportStructure: [Validators.required, Validators.min(0)],
        overallHeightOfStructure: [Validators.required, Validators.min(0)],
        structureTypeCode: [Validators.required],
        areaOfOperationText: [],
        correspondingFixedLocation: [],
        siteStatus: [],
        clearanceIndicator: [Validators.required],
        quietZoneNotificationDate: [],
        quietZoneConsent: [],
        nepa: [Validators.required],
        mexicoClearanceIndicator: [Validators.required],
    } as const;

    readonly requiredControlValidatorsSpecificToLocationType: Partial<
        ScheduleDStepControlsMap<ScheduleDLocationType>
    > = {
        A: ['correspondingFixedLocation'],
        C: ['county'],
    } as const;

    /**
     * Gets the form definition for Schedule D.
     *
     * NOTE: Any validators that are always present regardless of the type of location
     * are added here. Validators that are specific to the type of location are added
     * to `controlValidators`.
     */
    getFormDefinition(): FormGroup<FormGroupType<ScheduleDFormModel>> {
        return new FormGroup<FormGroupType<ScheduleDFormModel>>({
            id: new FormControl<number | null>(null),
            actionCode: new FormControl<ActionCode | null>(ActionCode.Add),
            isDeleted: new FormControl<boolean | null>(null),
            tempId: new FormControl<string | null>(null),
            isBeingEdited: new FormControl<boolean>(false),
            number: new FormControl<number | null>(null),
            locationTypeCode: new FormControl<string | null>(
                null,
                Validators.required,
            ),
            areaOfOperationCode: new FormControl<string | null>(null),
            locationName: new FormControl<string | null>(null),
            towerRegistrationNumber: new FormControl<string | null>(null),
            requireEWAToRegisterTower: new FormControl<boolean | null>(null),
            latitude: new FormControl<string | null>(null),
            longitude: new FormControl<string | null>(null),
            radius: new FormControl<string | null>(null),
            streetAddress: new FormControl<string | null>(null),
            city: new FormControl<string | null>(null),
            state: new FormControl<string | null>(null),
            county: new FormControl<string | null>(null),
            groundElevation: new FormControl<string | null>(null),
            heightOfSupportStructure: new FormControl<string | null>(null),
            overallHeightOfStructure: new FormControl<string | null>(null),
            structureTypeCode: new FormControl<string | null>(null),
            areaOfOperationText: new FormControl<string | null>(null),
            correspondingFixedLocation: new FormControl<number | null>(null),
            siteStatus: new FormControl<string | null>(null),
            clearanceIndicator: new FormControl<boolean | null>(null),
            quietZoneNotificationDate: new FormControl<string | null>(null),
            quietZoneConsent: new FormControl<boolean | null>(null),
            nepa: new FormControl<boolean | null>(null),
            mexicoClearanceIndicator: new FormControl<boolean | null>(null),
        });
    }

    /**
     * Gets the type of location based on the form value.
     * @param form the form to check
     * @returns the type of location
     */
    getLocationType(
        form: FormGroup<FormGroupType<ScheduleDFormModel>>,
    ): ScheduleDLocationType | null {
        if (this.isFixed(form)) {
            return 'fixed';
        }

        const nonFixedCodes = [
            AreaOfOperationCodes.kmraAroundAFixedLocation,
            AreaOfOperationCodes.countywide,
            AreaOfOperationCodes.nationwide,
            AreaOfOperationCodes.other,
            AreaOfOperationCodes.kmraAroundACenterpoint,
            AreaOfOperationCodes.statewide,
            AreaOfOperationCodes.citywide,
            AreaOfOperationCodes.continentalUS,
            AreaOfOperationCodes.landMobileControlStation,
        ];

        const match = nonFixedCodes.find((code) =>
            this.isNonFixedOfType(form, code),
        );

        if (match) {
            return match;
        }

        return null;
    }

    /**
     * Finds schedule Ds that have a value for correspondingFixedLocation (CFL),
     * if the schedule D that corresponds to that control value is not fixed, the CFL value is cleared.
     */
    fixCorrespondingLocationsForScheduleDs(
        scheduleDs: FormArray<FormGroup<FormGroupType<ScheduleDFormModel>>>,
    ): void {
        const locationsWithCorrespondingFixedLocations =
            scheduleDs.controls.filter(
                (scheduleD) => scheduleD.value.correspondingFixedLocation,
            );

        locationsWithCorrespondingFixedLocations.forEach((scheduleD) => {
            const correspondingFixedLocation = scheduleDs.controls
                .filter(
                    (location) =>
                        location.value.number !== scheduleD.value.number,
                )
                .find(
                    (correspondingLocation) =>
                        correspondingLocation.value.number ===
                            scheduleD.value.correspondingFixedLocation &&
                        this.isFixed(correspondingLocation),
                );

            if (!correspondingFixedLocation) {
                scheduleD.controls.correspondingFixedLocation.patchValue(null);
            }
        });
    }

    /**
     * Gets the 'reduced' type of location based on the form value.
     * @param form the form to check
     * @returns either 'fixed' or 'non-fixed'
     */
    getBinaryLocationType(
        form: FormGroup<FormGroupType<ScheduleDFormModel>>,
    ): ScheduleDBinaryLocationType {
        return this.isFixed(form) ? 'fixed' : 'non-fixed';
    }

    /**
     * Returns whether the form value is valid based on the type of location.
     * @param form the form to check
     * @returns true if the form is valid for the type of location, false otherwise
     */
    isFormValidForLocationType(
        form: FormGroup<FormGroupType<ScheduleDFormModel>>,
    ): Observable<boolean> {
        const type = this.getLocationType(form);
        if (!type) {
            return of(false);
        }

        this.removeRequiredValidatorsNotInType(
            form,
            type,
            this.locationTypeToControlsWithValidatorsMapping,
        );

        // Only add validators if the form is being added or modified.
        // This prevents validations from being run and displayed on locations
        // coming from the API (unless they are/once were modified).
        if (this.isAdded(form) || this.isModified(form)) {
            // Add validators for controls that are part of the current type
            this.locationTypeToControlsWithValidatorsMapping[type].forEach(
                (controlKey) => {
                    const control = form.get(controlKey);
                    if (!control) {
                        return;
                    }

                    control.addValidators(this.controlValidators[controlKey]);

                    if (
                        this.requiredControlValidatorsSpecificToLocationType[
                            type
                        ] &&
                        this.requiredControlValidatorsSpecificToLocationType[
                            type
                        ].includes(controlKey)
                    ) {
                        control.addValidators(Validators.required);
                    }
                },
            );
        }

        let isValid = true;
        this.locationTypeToControlsWithValidatorsMapping[type].forEach(
            (controlKey) => {
                const control = form.get(controlKey);
                if (!control) {
                    isValid = false;
                    return;
                }

                control.updateValueAndValidity({ emitEvent: false });

                if (
                    !control.valid &&
                    !(
                        controlKey === towerRegistrationNumberControl &&
                        !control.dirty
                    )
                ) {
                    isValid = false;
                }
            },
        );

        // If the location can have the validators for both latitude and longitude, we need to validate that the coordinates
        // are within the US or its territories.
        const canHaveValidatorsForLatitudeAndLongitude =
            this.canHaveValidatorsForField(form, 'latitude') &&
            this.canHaveValidatorsForField(form, 'longitude');
        if (canHaveValidatorsForLatitudeAndLongitude) {
            const request: CoordinateIsInUSOrTerritoriesRequest = {
                latitude: Number(form.controls.latitude.value),
                longitude: Number(form.controls.longitude.value),
            };

            return this.validationService
                .coordinateIsInUSOrTerritories(request)
                .pipe(
                    map((response) => {
                        return isValid && response.isValid;
                    }),
                );
        }

        return of(isValid);
    }

    /**
     * Removes required validators for controls that are not part of the current type.
     *
     * @param form the form group containing the schedule D controls
     * @param type the current location type for which to remove validators
     * @param stepControlMap the mapping of location types to their respective controls
     */
    removeRequiredValidatorsNotInType<T extends string | number | symbol>(
        form: FormGroup<FormGroupType<ScheduleDFormModel>>,
        type: T,
        stepControlMap: ScheduleDStepControlsMap<T>,
    ): void {
        const otherTypes = Object.keys(
            this.locationTypeToControlsWithValidatorsMapping,
        ).filter((key) => key !== type) as ScheduleDLocationType[];

        // Remove required validators for controls that are not part of the current type
        otherTypes.forEach((otherType) => {
            this.locationTypeToControlsWithValidatorsMapping[otherType].forEach(
                (control) => {
                    if (stepControlMap[type].includes(control)) {
                        return;
                    }

                    form.get(control)?.removeValidators(Validators.required);
                },
            );

            const requiredControlsOnlyForOtherType =
                this.requiredControlValidatorsSpecificToLocationType[otherType];
            if (requiredControlsOnlyForOtherType) {
                Object.values(requiredControlsOnlyForOtherType).forEach(
                    (controlKey) => {
                        if (
                            this.requiredControlValidatorsSpecificToLocationType[
                                type
                            ]?.includes(controlKey)
                        ) {
                            return;
                        }

                        form.get(controlKey)?.removeValidators(
                            Validators.required,
                        );
                    },
                );
            }
        });
    }

    /**
     * Finds whether the schedule D is valid.
     *
     * @param scheduleD the schedule D to check
     * @returns true if the schedule D is valid, false otherwise
     */
    isScheduleDValid(scheduleD: ScheduleDFormModel): Observable<boolean> {
        const form = this.getFormDefinition();
        form.patchValue(scheduleD);

        return this.isFormValidForLocationType(form);
    }

    /*
     * Determines if the location type code is fixed.
     */
    isFixed(form: FormGroup<FormGroupType<ScheduleDFormModel>>): boolean {
        return (
            form.controls.locationTypeCode.value !== null &&
            form.controls.locationTypeCode.value === LocationTypeCodes.fixed
        );
    }

    /*
     * Determines if the location type code is non-fixed.
     */
    isNonFixed(form: FormGroup<FormGroupType<ScheduleDFormModel>>): boolean {
        return (
            form.controls.locationTypeCode.value !== null &&
            form.controls.locationTypeCode.value !== LocationTypeCodes.fixed
        );
    }

    /*
     * Determines if the location type code is fixed.
     */
    isNonFixedOfType(
        form: FormGroup<FormGroupType<ScheduleDFormModel>>,
        type: AreaOfOperationCode,
    ): boolean {
        return (
            form.controls.areaOfOperationCode.value !== null &&
            form.controls.areaOfOperationCode.value === type
        );
    }

    isAdded(scheduleD: FormGroup<FormGroupType<ScheduleDFormModel>>): boolean {
        return scheduleD.controls.actionCode.value === ActionCode.Add;
    }

    isModified(
        scheduleD: FormGroup<FormGroupType<ScheduleDFormModel>>,
    ): boolean {
        return scheduleD.controls.actionCode.value === ActionCode.Modify;
    }

    /**
     * Patches the form value with the response. This is necessary because the API response types are different from the form types.
     * The form values are strings, but the API response values are numbers because it gets serialized as numbers.
     */
    patchFormValue(
        form: FormArray<FormGroup<FormGroupType<ScheduleDFormModel>>>,
        response: ScheduleD[],
    ): void {
        form.patchValue(this.getFormModelValue(response));
    }

    /**
     * Gets the value of the form array. Some values need to be set to null if they are empty strings.
     * @param form
     * @returns
     */
    getValue(
        form: FormArray<FormGroup<FormGroupType<ScheduleDFormModel>>>,
    ): ScheduleD[] {
        return form.getRawValue().map((scheduleD) => {
            return {
                ...scheduleD,
                latitude: tryParseToNumber(scheduleD.latitude),
                longitude: tryParseToNumber(scheduleD.longitude),
                radius: tryParseToNumber(scheduleD.radius),
                groundElevation: tryParseToNumber(scheduleD.groundElevation),
                heightOfSupportStructure: tryParseToNumber(
                    scheduleD.heightOfSupportStructure,
                ),
                overallHeightOfStructure: tryParseToNumber(
                    scheduleD.overallHeightOfStructure,
                ),
            };
        });
    }

    getFormModelValue(response: ScheduleD[]): ScheduleDFormModel[] {
        return response.map((scheduleD) => {
            return {
                ...scheduleD,
                isDeleted:
                    scheduleD.actionCode === ActionCode.Delete ? true : null,
                tempId: null,
                latitude: scheduleD.latitude?.toString() ?? null,
                longitude: scheduleD.longitude?.toString() ?? null,
                radius: scheduleD.radius?.toString() ?? null,
                groundElevation: scheduleD.groundElevation?.toString() ?? null,
                heightOfSupportStructure:
                    scheduleD.heightOfSupportStructure?.toString() ?? null,
                overallHeightOfStructure:
                    scheduleD.overallHeightOfStructure?.toString() ?? null,
                isBeingEdited: false,
            };
        });
    }

    markActionCodeOnScheduleDChange(
        control: FormControl,
        scheduleDs: FormArray<FormGroup<FormGroupType<ScheduleDFormModel>>>,
        scheduleDsResponse: ScheduleD[],
    ): boolean {
        return this.schedulesDAndHSharedFormService.markActionCodeOnChange(
            control,
            scheduleDs,
            this.getFormModelValue(scheduleDsResponse),
            [
                ...this.schedulesDAndHSharedFormService.defaultOmitFields,
                nameOf<ScheduleDFormModel>('isBeingEdited'),
            ],
        );
    }

    /**
     * Determines if a given field in the ScheduleD form can have validators based on the location type.
     *
     * @param scheduleD The form group representing a ScheduleD entry.
     * @param fieldName The name of the field to check for validators.
     * @returns A boolean indicating whether the field can have validators based on the location type.
     */
    private canHaveValidatorsForField(
        scheduleD: FormGroup<FormGroupType<ScheduleDFormModel>>,
        fieldName: keyof ScheduleDFormModel,
    ): boolean {
        const locationType: ScheduleDLocationType | null =
            this.getLocationType(scheduleD);

        return (
            locationType !== null &&
            (this.locationTypeToControlsWithValidatorsMapping[
                locationType
            ]?.includes(fieldName) ??
                false)
        );
    }
}
